import { TableCell, TableRow } from "@material-ui/core";
import React from "react";
import { useIntl } from "react-intl";
import { ExamTypeTranslationKey } from "../../../../model/Exam";
import { ModuleExamForSignupViewModel } from "../../../../model/ExamSignup/ExamSessionForSignupViewModel";
import { ModuleExamDetailsViewModel } from "../../../../model/ExamSignup/ModuleExamDetailsViewModel";
import { ModuleSelectionExamDetails } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import { GetTranslatedValueIntl } from "../../../../model/Shared/TranslatedValue";
import { GetTranslation } from "../../../../translations/GetTranslation";
import ExamFullSelectionCells from "./SelectionCells/ExamFullSelectionCells";
import NormalSelectionCells from "./SelectionCells/NormalSelectionCells";
import TermPaperSelectionCells from "./SelectionCells/TermPaperSelectionCells";
import { Language } from "../../../../data/constants";
import LanguageSelectorCell from "./SelectionCells/LanguageSelectorCell";
import StateIcon, { StateIconType } from "../../../common/icons/StateIcon";

type ExamLocationAndDateStepRowExamProps = {
  referenceExam: ModuleExamForSignupViewModel;
  selectedLocationId?: string;
  selectedExamDayId?: string;
  selectedLanguage?: Language;
  examPossibleDetails?: ModuleExamDetailsViewModel;
  updateExamDetails: (examDetails: ModuleSelectionExamDetails) => void;
  onLanguageChange: (language: Language, examId: string) => void;
  disabled: boolean;
  availableLanguages?: Language[];
  isLoading?: boolean;
  paid: boolean;
};

const ExamLocationAndDateStepRowExam = ({
  referenceExam,
  selectedLocationId,
  selectedExamDayId,
  selectedLanguage,
  examPossibleDetails,
  updateExamDetails,
  onLanguageChange,
  disabled,
  availableLanguages,
  isLoading,
  paid,
}: ExamLocationAndDateStepRowExamProps) => {
  const [locationId, setLocationId] = React.useState(selectedLocationId);
  const intl = useIntl();
  const [language, setLanguage] = React.useState(selectedLanguage ?? availableLanguages?.[0]);

  const selectedLocationDetails = examPossibleDetails?.availableLocations.find((l) => l.locationId === locationId);

  const onDayChange = (examDayId: string) => {
    if (locationId && examDayId) {
      updateExamDetails({
        examId: referenceExam.id,
        locationId,
        examDayId,
        locationName: selectedLocationDetails?.name,
        examDayDate: selectedLocationDetails?.examDays.find((d) => d.examDayId === examDayId)?.date,
        examType: referenceExam.type,
        language,
        isFullyBooked: false,
      });
    }
  };

  const onLocationChange = (newLocationId: string) => {
    setLocationId(newLocationId);
    updateExamDetails({
      examId: referenceExam.id,
      locationId,
      examDayId: undefined,
      locationName: selectedLocationDetails?.name,
      examDayDate: undefined,
      examType: referenceExam.type,
      language,
      isFullyBooked: false,
    });
  };

  const handleLanguageChange = (newLanguage: Language) => {
    onLanguageChange(newLanguage, referenceExam.id);
    setLanguage(newLanguage);
    setLocationId(undefined);
    updateExamDetails({
      examId: referenceExam.id,
      locationId: undefined,
      examDayId: undefined,
      locationName: undefined,
      examDayDate: undefined,
      examType: referenceExam.type,
      language: newLanguage,
      isFullyBooked: false,
    });
  };

  const isTermPaper = examPossibleDetails && examPossibleDetails.noLocationRequired;
  const isExamFull =
    !isTermPaper && (!examPossibleDetails || examPossibleDetails.availableLocations.length === 0) && !paid;
  const isOk = !isLoading && !isTermPaper && !isExamFull && selectedLanguage;

  return (
    <TableRow>
      <TableCell />
      <TableCell>{GetTranslatedValueIntl(intl, referenceExam.name)}</TableCell>
      <TableCell>{GetTranslation(intl, ExamTypeTranslationKey[referenceExam.type])}</TableCell>

      {isLoading && <TableCell colSpan={3} />}
      {isTermPaper && <TermPaperSelectionCells />}
      {!isLoading && !isTermPaper && (
        <LanguageSelectorCell
          selectedLanguage={language}
          onLanguageChange={handleLanguageChange}
          disabled={disabled}
          availableLanguages={availableLanguages}
        />
      )}
      {isExamFull && <ExamFullSelectionCells />}
      {isOk && (
        <NormalSelectionCells
          selectedLocationId={locationId}
          selectedExamDayId={selectedExamDayId}
          availableLocations={examPossibleDetails?.availableLocations ?? []}
          onDayChange={onDayChange}
          onLocationChange={onLocationChange}
          disabled={disabled}
        />
      )}
      <TableCell>
        {(!isOk && !isTermPaper) || (!examPossibleDetails?.noLocationRequired && (!selectedLocationId || !selectedExamDayId)) ? (
          <StateIcon state={isExamFull ? StateIconType.FullyBooked : StateIconType.Incomplete} />
        ) : (
          <StateIcon state={paid ? StateIconType.Paid : StateIconType.Completed} />
        )}
      </TableCell>
    </TableRow>
  );
};

export default ExamLocationAndDateStepRowExam;
